import axios, { AxiosInstance } from 'axios';
import { persistor } from '../store';
import Toast from '../components/Toast';
import { toast } from 'react-toastify';
import { getIp } from './utils/getIp';
import getPlatform from '../services/utils/getPlatform';
import getDevice from '../services/utils/getDevice';
import queries from './graphqlAnalyticQueries';
import { Config } from '../config';

class AnalyticService {
  private instance: AxiosInstance;
  private queries: typeof queries;
  private idConfig: string;
  constructor() {
    this.instance = axios.create({
      baseURL: process.env.REACT_APP_ANA_API || '/analytic/',
    });

    this.instance.interceptors.response.use(
      (response) => {
        if (response.data.errors) {
          if (response.data.errors[0].message === 'Guid expirada')
            persistor.purge();
          else
            toast.error(
              Toast({
                title: 'Erro',
                description: response.data.errors[0].message,
              })
            );
        }
        return response;
      },
      (error) => {
        if (error && error.response && 401 === error.response.status) {
          persistor.purge();
        } else {
          return Promise.reject(error);
        }
      }
    );
    this.queries = queries;
    this.idConfig = Config.ID_CONFIG;
  }

  setToken = (token: string) =>
    (this.instance.defaults.headers.Authorization = `${token}`);

  createSession = async (urlEntrada: string): Promise<string> => {
    const navegador = getPlatform();
    const tipoDispositivo = getDevice();
    const idConfig = this.idConfig;

    let ip: string | undefined = undefined;

    try {
      ip = await getIp();
    } catch {
      ip = '';
    } finally {
      const result = await this.instance.post('/auth/generateGuid', {
        ip,
        navegador,
        tipoDispositivo,
        urlEntrada,
        idConfig,
      });
      return result.data;
    }
  };

  updateSession = async (cpf: string): Promise<string> => {
    const result = await this.instance.post('/auth/updateGuidCpf', { cpf });
    return result.data;
  };

  endSession = async (): Promise<string> => {
    const result = await this.instance.post('/auth/endSession');
    return result.data;
  };

  navigationUrl = async (
    data: GQL.INavigationInput
  ): Promise<GQL.INavigationResponse> => {
    const response = await this.instance.post(
      '/graphql',
      this.queries.setNavigation(data)
    );
    if (response.data.errors?.length)
      throw new Error(response.data.errors[0].message);
    return (
      (response.data.data as GQL.IMutation).setNavigation ||
      ({} as GQL.INavigationResponse)
    );
  };

  setNegotiation = async (
    data: GQL.IInsertNegotiationModel
  ): Promise<string> => {
    await this.instance.post('/graphql', this.queries.setNegotiation(data));
    return 'ok';
  };

  setPayment = async (data: GQL.IPaymentInput): Promise<string> => {
    await this.instance.post('/graphql', this.queries.setPayment(data));
    return 'ok';
  };

  setError = async (
    data: GQL.ISetErrorOnMutationArguments
  ): Promise<GQL.ISetErrorResponse> => {
    const response = await this.instance.post(
      '/graphql',
      this.queries.setError(data, this.idConfig)
    );
    if (response.data.errors?.length)
      throw new Error(response.data.errors[0].message);
    return (
      (response.data.data as GQL.IMutation).setError ||
      ({} as GQL.ISetErrorResponse)
    );
  };
}

export default new AnalyticService();
